import queryString from '../../queryString';
import { Patient } from './models/Patient';
import { PatientSearchResultDetails } from './models/PatientSearchResultDetails';
import { PatientPhoto } from './models/PatientPhoto';
import { BaseService } from '../../base-service'
import { CourseExpiryFilterSubType, ExpiryFilterType } from "./models/ExpiryFilterType";
import { ChartStatusFilter } from './models/ChartStatusFilter';
import { PatientAllergyInfo } from './models/PatientAllergyInfo';
import { HealthIdentifierService } from '../../healthcareidentifier/healthcareidentifier-service'
import { VerifyIhiSearchRequest} from '../../healthcareidentifier/models/VerifyIhiSearchRequest';
import { IndividualHealthcareIdentifier } from '../../healthcareidentifier/models/IndividualHealthcareIdentifier';
import { IhiVerificationResult } from '../../healthcareidentifier/models/IhiVerificationResult';
import { getApiConfig } from '../../../config/api.config';
import { getFromStorage } from '../../../config/auth.config';


export interface PatientsSearchRequest {
    query?: string | null;
    limit?: number;
    status?: string
    page?: number;
    residentStatus?: string;
    expiryFilterType: ExpiryFilterType;
    courseExpiryFilterSubType?: CourseExpiryFilterSubType;
    chartStatus?: ChartStatusFilter;
    facilities?:(string | undefined)[];
}

export interface GetPatientRequest {
    authoringSystem: string;
    authoringSystemForeignKey: string;
}



class PatientApiV1 {

    async search(request: PatientsSearchRequest) {
        const path = `/api/v1/patients/search?${queryString(request)}`;
        let response = await BaseService.AsyncGet(path);
        return response as PatientSearchResultDetails;
    }

    async getPatient(authoringSystem: string, authoringSystemForeignKey: string) {
        const path = `/api/v1/patients/${authoringSystem}/${authoringSystemForeignKey}`;
        let response = await BaseService.AsyncGet(path);

        return response as Patient;
    }

    async getPatientAllergyInfo(authoringSystem: string, authoringSystemForeignKey: string): Promise<PatientAllergyInfo> {
        const path = `/api/v1/patients/${authoringSystem}/${authoringSystemForeignKey}/get-allergies`;
        let response = await BaseService.AsyncGet(path);

        return response as PatientAllergyInfo;
    }

    async getPatientPhoto(authoringSystem: string, authoringSystemForeignKey: string) {
        const path = `/api/v1/patients/photo/${authoringSystem}/${authoringSystemForeignKey}`;

        let response = await BaseService.getOrAdd('RESIDENT_PHOTOS',path);
        return response as PatientPhoto;
    }


    async getIndividualHealthcareIdentifier(authoringSystem: string, authoringSystemForeignKey: string) {
        
        let verifyPath = `/api/v1/patients/ihi/verify-required/${authoringSystem}/${authoringSystemForeignKey}`;
        let response = await BaseService.AsyncGet(verifyPath);

        if(response == false)
        {
            //IHI does not need to verified.  Get value from database.

            let getIhiPath = `/api/v1/patients/ihi/${authoringSystem}/${authoringSystemForeignKey}`;
            let ihiResult = await BaseService.AsyncGet(getIhiPath) as IndividualHealthcareIdentifier;

            let ihi:  IndividualHealthcareIdentifier = { 
                number : this.formatIhiNumber(ihiResult.number),
                status :  this.capitalizeFirstLetter(ihiResult.status), 
                recordStatus : this.capitalizeFirstLetter(ihiResult.recordStatus)
            };

            return ihi;
        }
        else
        {
            //IHI needs to be verified.  
            //First generate the VerifyIhiSearchRequest
            let requestPath = `/api/v1/patients/ihi/create-search-request/${authoringSystem}/${authoringSystemForeignKey}`;
            let verifyIhiSearchRequest = await BaseService.AsyncPost(requestPath, null ) as VerifyIhiSearchRequest;

            // As of now , in prod none of the organisation is configured with HIPO nummber .This checking stops sending IHI validation
            if(verifyIhiSearchRequest.hpioNumber === null || verifyIhiSearchRequest.hpioNumber === "")
            {
                let ihi:  IndividualHealthcareIdentifier = { 
                    number : "",
                    status :  "", 
                    recordStatus : ""
                };

                return ihi;
            }

            //Then send this request to the HealthIdentifier API
            let hiPath = `/api/v1/ihi/verify-search`;
            let response = await HealthIdentifierService.AsyncPost(hiPath, verifyIhiSearchRequest )
            

            if(response != null)
            {
                let ihiVerificationResult = response as IhiVerificationResult

                let ihi:  IndividualHealthcareIdentifier = { 
                    number : this.formatIhiNumber(ihiVerificationResult.number),
                    status :  this.capitalizeFirstLetter(ihiVerificationResult.status), 
                    recordStatus : this.capitalizeFirstLetter(ihiVerificationResult.recordStatus)
                };

                return ihi;
            }
            else{

                let ihi:  IndividualHealthcareIdentifier = { 
                    number : "",
                    status :  "", 
                    recordStatus : ""
                };

                return ihi;
                
            }
        }
    }

    formatIhiNumber(number?: string | null)    {
        
        //The Ihi number should be in the format of 'http://ns.electronichealth.net.au/id/hi/ihi/1.0/8003608500012182'
        //So we want to extract the number part.  Then format with a space in between every 4 chars.

        const emptyIhi = "8003 ____ ____ ____";

        if(number == null)
            return emptyIhi;

        if (number.indexOf('/') > 0)
            number = number.substring(number.lastIndexOf('/') + 1);

        if(number.length != 16)
            return emptyIhi;

        return `${number.substring(0, 4)} ${number.substring(4, 8)} ${number.substring(8, 12)} ${number.substring(12, 16)}`; 
    }

    capitalizeFirstLetter(value: string) {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    getMedchartPrintPDFPath(authoringSystem?: string, authoringSystemForeignKey?: string) {
        const query = `?qrCodeAllowed=${getApiConfig().featureFlags?.qrcode ?? false}`;
        let path = `/api/v1/print/${authoringSystem}/${authoringSystemForeignKey}${query}`;
        const sharedProviderPath = getFromStorage("providerId")
        if (sharedProviderPath) {
          path = path + "&" + sharedProviderPath;
        }
        return path;
    }

    async getDownloadFile(path: string) {

        let response = await BaseService.AsyncGetFile(path)
        return response.blob();
    }

    async saveCTGResponse(patientId: string, ctgResponse: string, authoringSystem?: string | null, authoringSystemForeignKey?: string | null) {
        const path = `/api/v1/patients/save-ctg-response`;
        let data = {
            authoringSystem: authoringSystem,
            authoringSystemForeignKey: authoringSystemForeignKey,
            patientId: patientId,
            ctgResponse: ctgResponse
        };
        let response = await BaseService.AsyncPost(path, data);

        return Boolean(response);
    }

    async saveAllergyAction(allergyhistoryId: string | null | undefined, actionstatus: boolean, authoringSystem?: string | null | undefined, authoringSystemForeignKey?: string | null | undefined) {
        const path = `/api/v1/patients/allergyactionhistory`;
        let data = {
            allergyhistoryId: allergyhistoryId,
            actionstatus: actionstatus,
            authoringSystem: authoringSystem,
            authoringSystemForeignKey: authoringSystemForeignKey,
        };
        return await BaseService.AsyncPost(path, data);
    }

    async saveAllergyActionConfirmation(allergyhistoryId: string | null | undefined, actionstatus: boolean, authoringSystem?: string | null | undefined, authoringSystemForeignKey?: string | null | undefined) {
        const path = `/api/v1/patients/allergyactionhistoryconfirmation`;
        let data = {
            allergyhistoryId: allergyhistoryId,
            actionstatus: actionstatus,
            authoringSystem: authoringSystem,
            authoringSystemForeignKey: authoringSystemForeignKey,
        };
        return await BaseService.AsyncPost(path, data);
    }

    async saveChartReviewConfirmation(pharmacyMedicationOrderId: string | null | undefined) {
        const path = `/api/v1/patients/save-chart-review-confirmation/${pharmacyMedicationOrderId}`;
        return await BaseService.AsyncPost(path, null);
    }

    async saveChartReviewAction(pharmacyMedicationOrderId: string | null | undefined, actionstatus: boolean, isConfirmed: boolean = false, authoringSystem?: string | null | undefined, authoringSystemForeignKey?: string | null | undefined) {
        const path = `/api/v1/patients/save-chart-review-action`;
        let data = {
            pharmacyMedicationOrderId: pharmacyMedicationOrderId,
            actionstatus: actionstatus,
            isConfirmed: isConfirmed
        };
        return await BaseService.AsyncPost(path, data);
    }
}


const patientService = new PatientApiV1();

export { patientService };
